import { IApiClient } from './IApiClient';
import { ProductionApiClient } from './ProductionApiClient';
import MockApiClient from '../../../mock/MockApiClient';

export type ApiClientMode = 'production' | 'mock';

function getApiClientMode(): ApiClientMode {
    if (typeof window !== 'undefined' && (window as any).VITE_USE_MOCK_API === 'true') {
        console.log('[ApiClientFactory] Mode: MOCK (window)');
        return 'mock';
    }

    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_USE_MOCK_API === 'true') {
        console.log('[ApiClientFactory] Mode: MOCK (env)');
        return 'mock';
    }

    if (typeof process !== 'undefined' && process.env?.VITE_USE_MOCK_API === 'true') {
        console.log('[ApiClientFactory] Mode: MOCK (process)');
        return 'mock';
    }

    console.log('[ApiClientFactory] Mode: PRODUCTION');
    return 'production';
}

function createApiClient(mode?: ApiClientMode): IApiClient {
    const clientMode = mode || getApiClientMode();

    switch (clientMode) {
        case 'mock':
            if (typeof window !== 'undefined' && (window as any).__mockApiClient__) {
                console.log('[ApiClientFactory] Reusing MockApiClient');
                return (window as any).__mockApiClient__;
            }
            console.log('[ApiClientFactory] Creating MockApiClient');
            return new MockApiClient();

        case 'production':
            console.log('[ApiClientFactory] Creating ProductionApiClient');
            return new ProductionApiClient();

        default:
            console.warn(`[ApiClientFactory] Unknown mode: ${clientMode}, using production`);
            return new ProductionApiClient();
    }
}

let _apiClientInstance: IApiClient | null = null;

function getApiClient(): IApiClient {
    if (!_apiClientInstance) {
        _apiClientInstance = createApiClient();
    }
    return _apiClientInstance;
}

/**
 * Global API client instance
 * Use this in your application code
 */
export const apiClient = new Proxy({} as IApiClient, {
    get(_target, prop) {
        const client = getApiClient();
        const value = (client as any)[prop];
        return typeof value === 'function' ? value.bind(client) : value;
    }
});

/**
 * Create a new API client instance (for testing)
 */
export function createApiClientInstance(mode?: ApiClientMode): IApiClient {
    return createApiClient(mode);
}

/**
 * Get the current API client mode
 */
export function getCurrentApiMode(): ApiClientMode {
    return getApiClientMode();
}

/**
 * Reset the singleton instance (for testing)
 */
export function resetApiClient(): void {
    _apiClientInstance = null;
    console.log('[ApiClientFactory] Singleton reset');
}
